/**
 * BookingSuccess.js - Confirmation page after successful payment
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, Calendar, MapPin, User, Phone, Mail, AlertCircle, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const pendingId = searchParams.get('pending_id');
  
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId || !pendingId) {
        setError('Invalid payment session');
        setLoading(false);
        return;
      }

      try {
        const res = await api.get(`/checkout/verify/${sessionId}?pending_id=${pendingId}`);
        setResult(res.data);
      } catch (error) {
        setError(error.response?.data?.detail || 'Payment verification failed');
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId, pendingId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[var(--primary)] mx-auto mb-4" />
          <p className="text-lg">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex items-center justify-center p-4">
        <div className="card max-w-md w-full p-6 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Payment Verification Failed</h1>
          <p className="text-[var(--text-muted)] mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="btn-primary">
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
      {/* Success Header */}
      <div className="bg-green-500 text-white py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
          >
            <CheckCircle className="w-20 h-20 mx-auto mb-4" />
          </motion.div>
          <h1 className="text-3xl font-bold mb-2">Payment Successful!</h1>
          <p className="text-lg opacity-90">Your booking has been confirmed</p>
        </div>
      </div>

      {/* Booking Details */}
      <div className="max-w-2xl mx-auto p-4 -mt-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6 shadow-lg"
        >
          {/* Reference Number */}
          <div className="bg-[var(--primary)]/10 rounded-lg p-4 mb-6 text-center">
            <p className="text-sm text-[var(--text-muted)]">Booking Reference</p>
            <p className="text-2xl font-bold text-[var(--primary)]" data-testid="reference-number">
              {result?.reference_number}
            </p>
          </div>

          {/* Details */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[var(--primary)] mt-0.5" />
              <div>
                <p className="font-medium">{result?.service_name}</p>
                <p className="text-sm text-[var(--text-muted)]">
                  {result?.scheduled_date} at {result?.scheduled_time}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center mt-0.5">
                <span className="text-white text-xs">$</span>
              </div>
              <div>
                <p className="font-medium">Amount Paid</p>
                <p className="text-lg font-bold text-green-600">
                  ${result?.amount_paid?.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Confirmation Email Notice */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg flex items-start gap-3">
            <Mail className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">Confirmation Sent</p>
              <p className="text-sm text-blue-600">
                A confirmation email has been sent to your email address with all the booking details.
              </p>
            </div>
          </div>

          {/* What's Next */}
          <div className="mt-6 border-t pt-6">
            <h3 className="font-bold mb-3">What happens next?</h3>
            <ul className="space-y-2 text-sm text-[var(--text-muted)]">
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                Your booking is confirmed and paid
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                The NC Help Hub team has been notified
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                A worker/driver will be assigned to your booking
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                You'll receive updates via email and text
              </li>
            </ul>
          </div>

          {/* Create Account CTA */}
          <div className="mt-6 p-4 bg-[var(--primary)]/5 rounded-lg text-center">
            <p className="font-medium mb-2">Want to track your booking?</p>
            <p className="text-sm text-[var(--text-muted)] mb-3">
              Create a free account to view booking status, manage appointments, and book faster next time.
            </p>
            <Link to="/register">
              <Button variant="outline" size="sm">
                Create Free Account
              </Button>
            </Link>
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-3">
            <Button 
              onClick={() => navigate('/')} 
              className="flex-1 btn-primary"
            >
              <Home className="w-4 h-4 mr-2" />
              Return Home
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BookingSuccess;
